import React, { useState, useEffect } from 'react';
import { getResponseHistory } from '../../api/questionnaire';

/**
 * VersionHistoryModal - Display complete version history for a response
 */
const VersionHistoryModal = ({ questionId, questionText, leadId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [questionId, leadId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getResponseHistory(leadId, questionId);
      // Backend returns {data: {...}, meta: {...}} on success
      if (response.data) {
        setHistory(response.data.history || []);
      } else {
        setError(response.message || 'Failed to load history');
      }
    } catch (err) {
      console.error('Error loading history:', err);
      // Handle error object from backend: {detail: {error: {message, code, details}}}
      const errorMessage = err.response?.data?.detail?.error?.message
        || err.response?.data?.detail
        || err.message
        || 'Failed to load version history';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  const getSourceBadge = (sourceType) => {
    const badges = {
      ai_generated: { bg: 'bg-primary-100', text: 'text-primary-800', label: 'AI Generated', icon: '🤖' },
      ai_updated: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'AI Updated', icon: '🔄' },
      user_edited: { bg: 'bg-green-100', text: 'text-green-800', label: 'User Edited', icon: '✏️' },
      not_answered: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Answered', icon: '❓' }
    };

    const badge = badges[sourceType] || badges.not_answered;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  const getConfidenceBadge = (confidence) => {
    if (!confidence) return null;

    const conf = parseFloat(confidence);
    let color = 'text-red-600';
    let bgColor = 'bg-red-100';
    let label = 'Low';

    if (conf >= 0.8) {
      color = 'text-green-600';
      bgColor = 'bg-green-100';
      label = 'High';
    } else if (conf >= 0.6) {
      color = 'text-yellow-600';
      bgColor = 'bg-yellow-100';
      label = 'Med';
    }

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${bgColor} ${color}`}>
        {Math.round(conf * 100)}% ({label})
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                Version History - Q{questionId}
              </h3>
              <p className="text-sm text-gray-600 mt-1">{questionText}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition flex-shrink-0 ml-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading version history...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No version history available</p>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900">Total Versions</h4>
                    <p className="text-2xl font-bold text-blue-700">{history.length}</p>
                  </div>
                  <div className="text-right">
                    <h4 className="text-sm font-semibold text-blue-900">Latest Change</h4>
                    <p className="text-sm text-blue-700">
                      {new Date(history[0].changed_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                {/* Version entries */}
                <div className="space-y-6">
                  {history.map((entry, index) => (
                    <div key={entry.id} className="relative pl-14">
                      {/* Version badge */}
                      <div className={`absolute left-0 top-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-blue-600' : 'bg-gray-400'
                      }`}>
                        v{entry.version}
                      </div>

                      {/* Version card */}
                      <div className={`border rounded-lg p-4 ${
                        index === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                      }`}>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              {index === 0 && (
                                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded">
                                  CURRENT
                                </span>
                              )}
                              {getSourceBadge(entry.source_type)}
                              {getConfidenceBadge(entry.confidence_score)}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">
                                {entry.changed_by_name || 'AI System'}
                              </span>
                              {' • '}
                              <span>{new Date(entry.changed_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* Change Reason */}
                        {entry.change_reason && (
                          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                            <span className="font-medium text-yellow-900">Reason: </span>
                            <span className="text-yellow-800">{entry.change_reason}</span>
                          </div>
                        )}

                        {/* Response Text */}
                        <div className="mb-3">
                          <div className="text-xs font-semibold text-gray-500 mb-1 uppercase">Response</div>
                          <div className="p-3 bg-gray-50 rounded border border-gray-200">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {entry.change_metadata?.new_value || entry.response_text || <span className="italic text-gray-500">No response</span>}
                            </p>
                          </div>
                        </div>

                        {/* Change Metadata (if exists) */}
                        {entry.change_metadata && (
                          <div className="text-xs text-gray-500">
                            <details className="cursor-pointer">
                              <summary className="font-medium hover:text-gray-700">
                                View change details
                              </summary>
                              <div className="mt-2 p-2 bg-gray-100 rounded">
                                <pre className="text-xs overflow-x-auto">
                                  {JSON.stringify(entry.change_metadata, null, 2)}
                                </pre>
                              </div>
                            </details>
                          </div>
                        )}

                        {/* Source References */}
                        {entry.source_references && entry.source_references.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs font-semibold text-gray-500 mb-1">Sources</div>
                            <div className="flex flex-wrap gap-1">
                              {entry.source_references.map((ref, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                >
                                  {ref.type || 'Source'}: {ref.meeting_id ? `Meeting ${ref.meeting_id.slice(0, 8)}` : ref.id}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {history.length > 0 && (
              <span>Showing {history.length} version{history.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryModal;
